import { type GuestQuoteLinkParams, guestQuoteLinkPath } from '@repo/api-contracts';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { deriveClientAccountStatus, useAuthStore, useMeQuery } from '@/entities/user';
import { pendingGuestQuoteStorage } from '@/shared/lib';
import {
  GUEST_QUOTE_CLAIM_MESSAGES,
  useGuestQuoteClaimNoticeStore,
} from './guest-quote-claim-notice.store';
import { useClaimGuestQuote } from './use-claim-guest-quote';

const SIGN_IN_PATH = '/sign-in';
const ONBOARDING_PATH = '/onboarding';

export interface UseOrderGuestQuoteResult {
  readonly order: () => Promise<void>;
  /** 계정 확인·연결 중 — 버튼을 잠근다. */
  readonly isPending: boolean;
}

/**
 * 링크 화면 [주문하기](결정 G11). 먼저 연결 대상을 기억한다. 그래야 어떤 길로 가입·인증을 마쳐도 앱 셸 훅이 이어 간다.
 * - 로그인 전: 로그인 화면으로 간다(가입 안내는 그 화면이 기억을 보고 띄운다). 로그인을 마친 ACTIVE 회원이 링크로 돌아오도록 `state.from` 에 링크 경로를 싣는다.
 * - 로그인함: 계정을 다시 읽는다(저장된 status 는 오래될 수 있다). ACTIVE 면 바로 연결한다. 인증이 남았으면 온보딩으로 가고, ACTIVE 가 된 뒤 앱 셸 훅이 연결한다.
 *   차단·탈퇴 계정은 users.me 가 거절해 실패 안내로 끝난다.
 */
export const useOrderGuestQuote = (params: GuestQuoteLinkParams): UseOrderGuestQuoteResult => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // 누를 때만 읽는다 — 링크 화면을 연 것만으로는 계정을 조회하지 않는다.
  const me = useMeQuery({ enabled: false });
  const { claim, isPending: isClaiming } = useClaimGuestQuote();
  const showNotice = useGuestQuoteClaimNoticeStore((state) => state.show);
  const [isChecking, setIsChecking] = useState(false);

  const order = async (): Promise<void> => {
    pendingGuestQuoteStorage.save(params);

    if (!isAuthenticated) {
      void navigate(SIGN_IN_PATH, {
        state: { from: guestQuoteLinkPath(params.quoteRequestId, params.signature) },
      });
      return;
    }

    setIsChecking(true);
    try {
      const result = await me.refetch();
      if (result.isError || result.data === undefined) {
        showNotice({ kind: 'FAILED', message: GUEST_QUOTE_CLAIM_MESSAGES.FAILED });
        return;
      }

      const status = deriveClientAccountStatus(result.data);
      if (status === 'ACTIVE') {
        await claim(params);
      } else if (status === 'PENDING_EMAIL' || status === 'PENDING_IDENTITY') {
        void navigate(ONBOARDING_PATH);
      }
    } finally {
      setIsChecking(false);
    }
  };

  return { order, isPending: isChecking || isClaiming };
};
